/**
 * Phase 7 device pairing (docs/android/PHASE-7-ARCHITECTURE.md SS3),
 * reachable from the account menu on the Android kiosk only (Layout.tsx,
 * gated on `isAndroidKiosk() && can('property:manage')`).
 *
 * Deliberately separate from DeviceSetup.tsx's "Set up this terminal"
 * screen: that is Phase 6's simple name-and-pin flow, already required
 * before the kiosk can be used at all, and is untouched by this file. This
 * modal is the newer, OPTIONAL fleet-monitoring identity - a device can run
 * POS Billing for months on Phase 6 registration alone, exactly like every
 * terminal in production today, and never touch this screen. What this adds
 * is a way to bring an already-running kiosk onto the admin Device
 * Management screen for real (status other than UNREGISTERED, health other
 * than permanent OFFLINE, battery/storage/kiosk-active telemetry) - none of
 * which Phase 6 registration or ordinary background sync ever populate,
 * because neither of them calls the Phase 7 device-facing endpoints this
 * modal and lib/deviceHeartbeat.ts do.
 *
 * Flow: an admin generates a single-use, time-boxed code from Device
 * Management's own "Generate pairing code" button and reads it out to
 * whoever is standing at the tablet; entering it here exchanges it for this
 * device's own credential (`X-Device-Id`/`X-Device-Secret`, deviceAuth.ts)
 * via `redeemDevicePairingCode()`, persists it (deviceCredential.ts), and
 * sends one heartbeat immediately so the fleet screen updates without
 * waiting for the next periodic one.
 */
import { useState, type FormEvent } from 'react';
import { Modal, TextInput, useToast } from '../components/ui';
import { getAndroidAppVersion, getDeviceInfo } from '../lib/androidBridge';
import { DeviceApiError, redeemDevicePairingCode } from '../lib/deviceApi';
import { setDeviceSecret } from '../lib/deviceCredential';
import { interimDeviceCode, persistedDeviceId } from '../lib/deviceId';
import { sendOneHeartbeat } from '../lib/deviceHeartbeat';
import { getActivePosDataSource } from '../lib/posData';

type Step = 'code' | 'working' | 'done';

/**
 * `[A-Z2-9]{8}` matches the server's own `redeemPairingCodeSchema`
 * (`@kortex/shared`) - checked here too so a mistyped code is caught before
 * the round-trip, not after.
 */
const CODE_PATTERN = /^[A-Z2-9]{8}$/;

export function DevicePairingModal({ onClose }: { onClose: () => void }) {
  const { push, fail } = useToast();
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>('code');
  const [error, setError] = useState<string | null>(null);

  const ready = CODE_PATTERN.test(code);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setStep('working');
    setError(null);
    try {
      const deviceId = persistedDeviceId();
      const info = getDeviceInfo();
      const { deviceSecret } = await redeemDevicePairingCode({
        code,
        deviceId,
        deviceCode: interimDeviceCode(deviceId),
        androidVersion: info?.androidVersion,
        appVersion: getAndroidAppVersion() ?? undefined,
      });
      setDeviceSecret(deviceSecret);

      // Best-effort - the periodic heartbeat (lib/deviceHeartbeat.ts) picks
      // up within its own interval regardless of whether this one succeeds
      // or the local data source isn't up yet for some reason.
      const source = getActivePosDataSource();
      if (source) await sendOneHeartbeat(source).catch(() => undefined);

      setStep('done');
      push('This device is now paired for fleet monitoring', 'ok');
    } catch (caught) {
      setStep('code');
      const message =
        caught instanceof DeviceApiError
          ? caught.message
          : 'Could not pair this device. Check your connection and try again.';
      setError(message);
      fail(caught);
    }
  };

  if (step === 'done') {
    return (
      <Modal
        title="Device paired"
        onClose={onClose}
        footer={<button className="btn primary" onClick={onClose}>Done</button>}
      >
        <p className="small">
          This device now reports its status to Device Management - it should show as registered
          the next time that screen refreshes, with health following within a few minutes.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title="Pair this device"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={step === 'working'}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="device-pairing-form"
            disabled={!ready || step === 'working'}
          >
            {step === 'working' ? 'Pairing…' : 'Pair device'}
          </button>
        </>
      }
    >
      <form id="device-pairing-form" onSubmit={(e) => void submit(e)} className="col gap-16">
        {error ? <div className="form-error">{error}</div> : null}
        <p className="small muted">
          Generate a pairing code from Device Management on the admin side, then enter it here
          before it expires. This is separate from signing in - it only connects this device to
          the fleet-monitoring screen, and nothing else about how it works changes.
        </p>
        <TextInput
          label="Pairing code"
          autoComplete="off"
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, '').slice(0, 8))}
          hint="8 characters, from an admin's Device Management screen."
        />
      </form>
    </Modal>
  );
}
